import {
  saveStudentAnswers,
  getUserResponse,
  submitExam as submitExamApi,
} from "@oneapp/data";
import type {
  SyncAdapter,
  SaveAnswerPayload,
  ExamProgressData,
  SubmissionType,
} from "../../../shared/examSession";
import { toISOWithOffset } from "../../../shared/utils";
import { serverNow } from "../../../shared/serverClock";
import { AnalyticsEvents, AnalyticsManager } from "@repo/analytics";

const ONE_ASM = { clientName: "oneAsm" } as const;

function getAnalytics() {
  try {
    return AnalyticsManager.getInstance();
  } catch {
    return null;
  }
}

export function createSyncAdapter(): SyncAdapter {
  return {
    async saveAnswers(
      studentId: string,
      examId: string,
      answers: SaveAnswerPayload[],
    ): Promise<void> {
      const requestBody = {
        student_id: Number(studentId),
        exam_id: examId,
        answered_questions: answers,
      };
      try {
        await saveStudentAnswers(requestBody, ONE_ASM);
        getAnalytics()?.track(AnalyticsEvents.SAVE_STUDENT_ANSWER_SUCCESS, 1, {
          ...requestBody,
        });
      } catch (err) {
        getAnalytics()?.track(AnalyticsEvents.SAVE_STUDENT_ANSWER_FAILURE, 1, {
          ...requestBody,
          error: err instanceof Error ? err.message : "Unknown error",
        });
        throw err;
      }
    },

    async fetchExamData(
      examId: string,
      studentId: string,
    ): Promise<ExamProgressData> {
      const response = await getUserResponse(examId, studentId, ONE_ASM);
      const data = response as unknown as {
        exam_id?: string;
        status?: string;
        started_at?: string;
        student_exam_end_time?: string;
        answered_questions?: Array<{
          question_id?: string;
          student_answer?: Record<string, unknown>;
          mark_for_review?: boolean;
        }>;
      };
      return {
        examId: data.exam_id ?? examId,
        status: data.status ?? "",
        startedAt: data.started_at ?? null,
        examEndTime: data.student_exam_end_time ?? null,
        answeredQuestions: (data.answered_questions ?? [])
          .filter((q) => !!q.question_id)
          .map((q) => ({
            questionId: q.question_id!,
            studentAnswer: q.student_answer ?? null,
            markForReview: q.mark_for_review ?? false,
          })),
      };
    },

    async submitExam(
      examId: string,
      studentId: string,
      submissionType: SubmissionType,
      examEndTime?: string,
      submittedAt?: string,
    ): Promise<void> {
      let submittedAtDate = submittedAt
        ? new Date(submittedAt)
        : new Date(serverNow());
      if (examEndTime) {
        const endTime = new Date(examEndTime);
        if (submittedAtDate > endTime) submittedAtDate = endTime;
      }
      const requestBody = {
        student_id: Number(studentId),
        exam_id: examId,
        submitted_at: toISOWithOffset(submittedAtDate),
        submission_type: submissionType,
      };
      try {
        await submitExamApi(requestBody, ONE_ASM);
        getAnalytics()?.track(AnalyticsEvents.SUBMIT_EXAM_SUCCESS, 1, {
          ...requestBody,
        });
      } catch (err) {
        getAnalytics()?.track(AnalyticsEvents.SUBMIT_EXAM_FAILURE, 1, {
          ...requestBody,
          error: err instanceof Error ? err.message : "Unknown error",
        });
        throw err;
      }
    },
  };
}
